import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { keyframes, style } from '@angular/animations';

import { NgStaticAnimationService } from '../../shared/services/static-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function backward(translateX: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	translateX = translateX ? translateX : 8;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				transform  				: `translateX(-${translateX}px)`,
				'transition-property' 	: 'transform',
				easing 					: 'ease-in',
				offset 					: 0
			}),
			style({
				transform  				: 'translateX(0)',
				'transition-property' 	: 'transform',
				easing 					: 'ease-in',
				offset 					: 1
			})
		]),
		end		: keyframes([
			style({
				transform  				: 'translateX(0)',
				'transition-property' 	: 'transform',
				easing 					: 'ease-out',
				offset 					: 0
			}),
			style({
				transform 				: `translateX(-${translateX}px)`,
				'transition-property' 	: 'transform',
				easing 					: 'ease-out',
				offset 					: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Backward on Event
 */
@Directive({
    selector: '[ngBackwardOnEvent]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgBackwardOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateX value is relative to the animation functionality
		if (this.effectOptions?.translateX && this.effectOptions?.translateX < 1) throw new Error(`BackwardOnEventDirective => ngOnInit => FATAL ERROR: Provided translateX value should be a positive number: ${this.effectOptions?.translateX}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: backward(this.effectOptions?.translateX),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * Backward on Mouse Hover
 */
@Directive({
    selector: '[ngBackwardOnMouseHover]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgBackwardOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateX value is relative to the animation functionality
		if (this.timingOptions?.translateX && this.timingOptions?.translateX < 1) throw new Error(`BackwardOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided translateX value should be a positive number: ${this.timingOptions?.translateX}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: backward(this.timingOptions?.translateX),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	translateX : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	translateX : number;
}
